/**
 * Where the till keeps the numbers it is allowed to issue.
 *
 * Specification: ../test/receipt-numbers.spec.ts. Master plan §2 D7, §4 M7.
 *
 * SAME `SqliteDriver`, SAME OPFS DATABASE as the outbox and the drawer journal,
 * for the reason `drawer-journal.ts` gives: one database to open, one set of
 * durability settings, and no second thing that can be half-migrated.
 *
 * DURABILITY IS THE ENTIRE POINT OF THIS FILE. The rule it exists to serve is
 * that `next_number` is persisted BEFORE the event that uses it is emitted, so
 * a crash burns a number rather than reissuing one. That is only true if the
 * write is actually durable — which is why this rides the store already
 * configured `synchronous = FULL` rather than a second one somebody tunes
 * differently later.
 *
 * KEYED BY PERIOD, because a block is a block of a SERIES IN A PERIOD. A till
 * holding 2026 numbers cannot spend them in 2027: the server would refuse the
 * assignment as outside the lease, and it would refuse it after the receipt was
 * already printed. So the period is part of the key, and the till can hold this
 * period's block and the next one at the same time.
 */

import type { SqliteDriver } from "@gf/kernel/sqlite";

/** One leased block, as the till holds it. */
export interface HeldBlock {
  readonly leaseId: string;
  readonly seriesKey: string;
  readonly period: string;
  readonly fromNumber: number;
  readonly toNumber: number;
  /** The next number to issue. Always within [fromNumber, toNumber + 1]. */
  readonly nextNumber: number;
  readonly expiresAt: Date;
}

interface Row {
  readonly lease_id: string;
  readonly series_key: string;
  readonly period: string;
  readonly from_number: number;
  readonly to_number: number;
  readonly next_number: number;
  readonly expires_at: number;
}

const toBlock = (row: Row): HeldBlock => ({
  leaseId: row.lease_id,
  seriesKey: row.series_key,
  period: row.period,
  fromNumber: row.from_number,
  toNumber: row.to_number,
  nextNumber: row.next_number,
  expiresAt: new Date(row.expires_at),
});

export class SqliteReceiptLeaseStore {
  readonly #driver: SqliteDriver;

  constructor(driver: SqliteDriver) {
    this.#driver = driver;
  }

  /**
   * Idempotent: every app start runs it.
   *
   * KEYED ON `lease_id`, NOT ON `(series, period)`, and that is a correction
   * rather than a preference. One row per period meant a top-up REPLACED the
   * block it topped up — burning whatever was left in it, which at a 20 %
   * low-water mark is up to a fifth of every block a till ever holds, as a gap,
   * every time. The spec caught it: numbers skipped from 3 straight to 4.
   *
   * So the till may hold several blocks at once and spends the OLDEST one
   * first, which is also what keeps the series monotonic.
   */
  async migrate(): Promise<void> {
    await this.#driver.exec(`
      CREATE TABLE IF NOT EXISTS receipt_lease (
        lease_id    TEXT    NOT NULL PRIMARY KEY,
        series_key  TEXT    NOT NULL,
        period      TEXT    NOT NULL,
        from_number INTEGER NOT NULL,
        to_number   INTEGER NOT NULL,
        next_number INTEGER NOT NULL,
        expires_at  INTEGER NOT NULL
      )
    `);
    await this.#driver.exec(
      `CREATE INDEX IF NOT EXISTS receipt_lease_series_idx
         ON receipt_lease (series_key, period, from_number)`,
    );
  }

  /** Remember a block. Re-putting the same lease is a no-op on its cursor. */
  async put(block: HeldBlock): Promise<void> {
    await this.#driver.run(
      `INSERT INTO receipt_lease
         (lease_id, series_key, period, from_number, to_number, next_number, expires_at)
       VALUES (?, ?, ?, ?, ?, ?, ?)
       ON CONFLICT (lease_id) DO NOTHING`,
      [
        block.leaseId,
        block.seriesKey,
        block.period,
        block.fromNumber,
        block.toNumber,
        block.nextNumber,
        block.expiresAt.getTime(),
      ],
    );
  }

  /**
   * Advance the OLDEST unexhausted block, durably, and say what was consumed.
   *
   * ONE STATEMENT, WITH `RETURNING`, and both halves of that matter.
   *
   * One statement, because the caller's whole guarantee rests on the advance
   * being a single durable write it can rely on having happened before it emits
   * anything.
   *
   * `RETURNING`, because without it there is no way to tell "I advanced the
   * cursor" from "the WHERE matched nothing". The first version read the row
   * back afterwards and inferred the number from the cursor — and on an
   * exhausted block that inference HANDED OUT THE LAST NUMBER A SECOND TIME.
   * The exact defect this module exists to prevent, in the function that exists
   * to prevent it. The spec caught it on the first run.
   */
  async take(seriesKey: string, period: string): Promise<{ number: number; leaseId: string } | null> {
    const rows = await this.#driver.all<{ taken: number; lease_id: string }>(
      `UPDATE receipt_lease SET next_number = next_number + 1
         WHERE lease_id = (
           SELECT lease_id FROM receipt_lease
            WHERE series_key = ? AND period = ? AND next_number <= to_number
            ORDER BY from_number LIMIT 1
         )
       RETURNING next_number - 1 AS taken, lease_id`,
      [seriesKey, period],
    );

    const row = rows[0];
    return row ? { number: row.taken, leaseId: row.lease_id } : null;
  }

  /** Every block for a period, oldest first. */
  async get(seriesKey: string, period: string): Promise<HeldBlock[]> {
    const rows = await this.#driver.all<Row>(
      `SELECT lease_id, series_key, period, from_number, to_number, next_number, expires_at
         FROM receipt_lease WHERE series_key = ? AND period = ? ORDER BY from_number`,
      [seriesKey, period],
    );
    return rows.map(toBlock);
  }

  /** Every block held, so the drawer close can return all of them. */
  async all(): Promise<HeldBlock[]> {
    const rows = await this.#driver.all<Row>(
      `SELECT lease_id, series_key, period, from_number, to_number, next_number, expires_at
         FROM receipt_lease ORDER BY period, series_key, from_number`,
      [],
    );
    return rows.map(toBlock);
  }

  async forget(leaseId: string): Promise<void> {
    await this.#driver.run(`DELETE FROM receipt_lease WHERE lease_id = ?`, [leaseId]);
  }
}
